import { useEffect, useState } from 'react';
import { useFetchReservations, useReservations } from '@repo/domain-service';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { useNavigate } from 'react-router-dom';

import PaymentBlikWaitContents from '@features/reservation/components/payment/PaymentBlikWaitContents';
import PaymentMethodsContents from '@features/reservation/components/payment/PaymentMethodsContents';
import { type FutureSpanPayment, type PaymentResultLocationState } from '@features/reservation/types';

import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const CONFIRMATION_POLL_INTERVAL_MILLISECONDS = 3_000;
// Mirrors the backend's `reservation.extension_lock.ttl_minutes`, for which the payment of the span stays valid.
const CONFIRMATION_TIMEOUT_MILLISECONDS = 5 * 60 * 1_000;

const testId = TestId.root('reservation.paymentFutureSpan');

// The backend sells the future span as an extension of the rental.
const FUTURE_SPAN_PAYMENT_RESULT: PaymentResultLocationState = { product: 'parkingSpotRentalExtension' };

interface Props {
  futureSpanPayment: FutureSpanPayment;
}

/**
 * `/reservation/payment` for a future span of a revolving reservation. The backend's `Reservation.order` can't
 * point at the span's own payment, so a BLIK payment is confirmed by watching the span become scheduled instead.
 */
export default function PaymentFutureSpanContents({ futureSpanPayment }: Props) {
  const navigate = useNavigate();
  const fetchReservations = useFetchReservations();
  const { reservations, loadingReservations } = useReservations({ id: futureSpanPayment.reservationId });
  const [awaitingConfirmation, setAwaitingConfirmation] = useState<boolean>(false);
  const reservation = reservations[0];

  useEffect(() => {
    if (loadingReservations || reservation) return;

    toast.danger(tr('reservations:span_payment_error.reservation_not_found'), testId.append('noReservationToast'));
    navigate(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS], { replace: true });
  }, [loadingReservations, reservation, navigate]);

  useEffect(() => {
    if (!awaitingConfirmation) return;

    const pollInterval = setInterval(() => {
      void (async () => {
        try {
          const [latestReservation] = await fetchReservations({ id: futureSpanPayment.reservationId });
          const paidSpan = latestReservation?.spans.find(
            (reservationSpan) => reservationSpan.id === futureSpanPayment.reservationSpanId
          );
          if (paidSpan?.status === 'scheduled') {
            navigate(ROUTE_PATHS[RouteName.RESERVATION_SUCCESS], { replace: true, state: FUTURE_SPAN_PAYMENT_RESULT });
          }
        } catch {
          // A failed poll is retried on the next tick; the timeout below ends the wait.
        }
      })();
    }, CONFIRMATION_POLL_INTERVAL_MILLISECONDS);

    const confirmationTimeout = setTimeout(() => {
      navigate(ROUTE_PATHS[RouteName.RESERVATION_FAILURE], { replace: true, state: FUTURE_SPAN_PAYMENT_RESULT });
    }, CONFIRMATION_TIMEOUT_MILLISECONDS);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(confirmationTimeout);
    };
    // Runs only when the wait starts; fetchReservations is recreated each render and must not restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingConfirmation, futureSpanPayment, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleFutureSpanPaymentInitiated = () => {
    setAwaitingConfirmation(true);
  };

  if (loadingReservations) return <PleaseWaitAnimation testId={testId} />;

  if (!reservation) return null;

  if (awaitingConfirmation) return <PaymentBlikWaitContents onGoBack={handleBack} />;

  return (
    <PaymentMethodsContents
      reservation={reservation}
      futureSpanPayment={futureSpanPayment}
      onFutureSpanPaymentInitiated={handleFutureSpanPaymentInitiated}
    />
  );
}
